import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Identity } from '@/types/identity';
import {
    getIdentityFallbackIcon,
    getIdentityLabel,
    identityToken,
} from '@/utils/identity';
import type { ActionMenuItem } from '@/components/EntityActionsMenu/EntityActionsMenu';
import { EntityAvatar } from '@/components/EntityAvatar/EntityAvatar';
import { IdentityHoverCard } from '@/components/IdentityHoverCard';
import {
    IDENTITY_CELL_BUTTON_CLASSES,
    IDENTITY_CELL_SQUARE_CLASSES,
    type IdentityAvatarSize,
} from './identityAvatarSize';

interface IdentityAvatarGridCellProps {
    identity: Identity;
    isSelected: boolean;
    showLabel: boolean;
    size: IdentityAvatarSize;
    onToggle: () => void;
    menuItems: ActionMenuItem[];
}

export function IdentityAvatarGridCell({
    identity,
    isSelected,
    showLabel,
    size,
    onToggle,
    menuItems,
}: IdentityAvatarGridCellProps) {
    const { t } = useTranslation();
    const FallbackIcon = getIdentityFallbackIcon(identity.kind);
    const label = getIdentityLabel(t, identity);

    return (
        <IdentityHoverCard identity={identity} menuItems={menuItems}>
            <button
                type="button"
                onClick={onToggle}
                aria-pressed={isSelected}
                aria-label={label}
                data-testid={`identity-cell-${identityToken(identity)}`}
                className={cn(
                    IDENTITY_CELL_BUTTON_CLASSES,
                    showLabel ? 'w-full' : IDENTITY_CELL_SQUARE_CLASSES,
                )}
            >
                <EntityAvatar
                    icon={<FallbackIcon weight="regular" />}
                    imageUrl={identity.thumbnailUrl}
                    shape="circle"
                    size={size}
                    selectionState={isSelected ? 'selected' : 'unselected'}
                />
                {showLabel && (
                    <span
                        className={cn(
                            'w-full truncate text-center text-sm font-normal',
                            identity.known ? 'text-white-100' : 'text-grey-300',
                        )}
                    >
                        {label}
                    </span>
                )}
            </button>
        </IdentityHoverCard>
    );
}
